import { supabase, TABLES, COLUMN_MAPS } from '../supabase';
import { Task, TaskStatus } from '../../types';
import { PAGINATION } from '../../utils/constants';

// ============================================
// Tasks Service
// ============================================

export const tasksApi = {
  /**
   * Get all tasks
   */
  async getAll(): Promise<Task[]> {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .select('*');
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.task.fromDb);
  },

  /**
   * Get tasks with pagination
   */
  async getPaginated(
    page = PAGINATION.DEFAULT_PAGE,
    limit = PAGINATION.DEFAULT_LIMIT
  ): Promise<{ data: Task[]; total: number }> {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.TASKS)
      .select('*', { count: 'exact' })
      .range(from, to);
    
    if (error) throw error;
    
    return {
      data: (data || []).map(COLUMN_MAPS.task.fromDb),
      total: count || 0,
    };
  },

  /**
   * Get a single task by ID
   */
  async getById(id: string): Promise<Task | null> {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .select('*')
      .eq('id', id)
      .maybeSingle();
    
    if (error) throw error;
    return data ? COLUMN_MAPS.task.fromDb(data) : null;
  },

  /**
   * Get tasks by project ID
   */
  async getByProject(projectId: string): Promise<Task[]> {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .select('*')
      .eq('project_id', projectId);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.task.fromDb);
  },

  /**
   * Get tasks by assignee
   */
  async getByAssignee(assigneeId: string): Promise<Task[]> {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .select('*')
      .eq('assignee_id', assigneeId);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.task.fromDb);
  },

  /**
   * Get tasks by status
   */
  async getByStatus(status: TaskStatus): Promise<Task[]> {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .select('*')
      .eq('status', status);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.task.fromDb);
  },

  /**
   * Get overdue tasks
   */
  async getOverdue(): Promise<Task[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .select('*')
      .lt('deadline', today)
      .neq('status', TaskStatus.DONE);
    
    if (error) throw error;
    return (data || []).map(COLUMN_MAPS.task.fromDb);
  },

  /**
   * Create a new task
   */
  async create(task: Task): Promise<void> {
    const dbData = COLUMN_MAPS.task.toDb(task);
    
    const { error } = await supabase
      .from(TABLES.TASKS)
      .insert(dbData);
    
    if (error) throw error;
  },

  /**
   * Update a task
   */
  async update(id: string, updates: Partial<Task>): Promise<void> {
    const dbUpdates: Record<string, any> = {};
    
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.assigneeId !== undefined) dbUpdates.assignee_id = updates.assigneeId;
    if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
    if (updates.checklist !== undefined) dbUpdates.checklist = updates.checklist;
    if (updates.deadline !== undefined) dbUpdates.deadline = updates.deadline;
    if (updates.estimateHours !== undefined) dbUpdates.estimate_hours = updates.estimateHours;

    const { error } = await supabase
      .from(TABLES.TASKS)
      .update(dbUpdates)
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Update task status
   */
  async updateStatus(id: string, status: TaskStatus): Promise<void> {
    await this.update(id, { status });
  },

  /**
   * Delete a task
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.TASKS)
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  /**
   * Delete all tasks for a project
   */
  async deleteByProject(projectId: string): Promise<void> {
    const { error } = await supabase
      .from(TABLES.TASKS)
      .delete()
      .eq('project_id', projectId);
    
    if (error) throw error;
  },

  /**
   * Bulk update task status
   */
  async bulkUpdateStatus(ids: string[], status: TaskStatus): Promise<void> {
    const { error } = await supabase
      .from(TABLES.TASKS)
      .update({ status })
      .in('id', ids);
    
    if (error) throw error;
  },
};

export default tasksApi;
